// apps/web/src/pages/estoque/saldos-clientes-aba.tsx — Fase 13, Plano 14 (EST-08, EST-11, D-13, D-23).
// Aba "Material de clientes" da tela de Saldos: um lote por linha, valorizado SÓ pelo valor da NF de entrada (nunca pelo
// custo médio da Pieltec, que fica na aba "Estoque próprio"). Busca, filtros (Cliente, Localização, "Mostrar itens sem
// saldo") e paginação de 50 são resolvidos no servidor; esta aba só apresenta. Os filtros moram na SaldosPage (URL).
import { Fragment, useCallback, useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import { ChevronDown, ChevronRight, MoreHorizontal, RefreshCw, Search } from 'lucide-react';
import type { ClienteLookupPv, ListaSaldosClientes, SaldoClienteLinha } from '@pieltec/contracts';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { formatarCustoUnitario, formatarQuantidadeEstoque, formatarValor } from '../../lib/estoque/formatacao';
import { TAMANHO_PAGINA_SALDOS, colunasVisiveis, estadoDaLista, type ColunasVisiveis } from '../../lib/estoque/saldos';
import {
  filtrosClientesAtivos,
  montarQuerySaldosClientes,
  textoUltimaMudanca,
  type FiltrosSaldosClientes,
} from '../../lib/estoque/saldos-clientes';

const ENDPOINT_CLIENTES_ESTOQUE = '/api/v1/estoque/lookups/clientes';
const APOIO_VALOR_NF = 'Valor pela NF de entrada — não entra no estoque próprio.';
const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

function larguraDaJanela(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/** Largura atual da janela: decide se Material e valores ficam na grade (>= 1024px) ou na linha expandida. */
function useColunasVisiveis(): ColunasVisiveis {
  const [largura, setLargura] = useState(larguraDaJanela);
  useEffect(() => {
    const atualizar = () => setLargura(larguraDaJanela());
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, []);
  return colunasVisiveis(largura);
}

function DadoExpandido({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

interface LinhaLoteProps {
  linha: SaldoClienteLinha;
  aberta: boolean;
  colunas: ColunasVisiveis;
  colSpan: number;
  onAlternar: () => void;
  onHistorico: (linha: SaldoClienteLinha) => void;
  /** Só `true` com estoque:movimentar: sem a permissão, a ação de transferir não é renderizada. */
  podeMovimentar: boolean;
  onTransferir: (linha: SaldoClienteLinha) => void;
}

function LinhaLote({ linha, aberta, colunas, colSpan, onAlternar, onHistorico, podeMovimentar, onTransferir }: LinhaLoteProps) {
  const idDetalhe = `lote-${linha.loteId}-detalhe`;

  function cliqueNaLinha(event: MouseEvent<HTMLTableRowElement>): void {
    if ((event.target as HTMLElement).closest('a,button,[role="menuitem"]')) return;
    onAlternar();
  }

  return (
    <Fragment>
      <tr onClick={cliqueNaLinha} className="cursor-pointer border-t hover:bg-slate-50">
        <td className="w-12 px-1 py-1">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberta}
            aria-controls={idDetalhe}
            aria-label={`${aberta ? 'Recolher' : 'Expandir'} lote ${linha.loteInternoPieltec}`}
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {aberta ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">{linha.loteInternoPieltec}</td>
        <td className="max-w-0 w-full px-3 py-2">
          <div className="truncate">{linha.clienteNome}</div>
        </td>
        {colunas.valor && (
          <td className="px-3 py-2">
            <div className="font-semibold tabular-nums">{linha.materialCodigo}</div>
            <div className="max-w-56 truncate text-muted-foreground" title={linha.materialNome}>{linha.materialNome}</div>
          </td>
        )}
        <td className="whitespace-nowrap px-3 py-2">{linha.localizacaoNome}</td>
        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">
          {formatarQuantidadeEstoque(linha.saldo, linha.unidade)}
        </td>
        {colunas.valor && (
          <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatarCustoUnitario(linha.valorUnitarioNf, '')}</td>
        )}
        {colunas.valor && <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatarValor(linha.valorNf)}</td>}
        <td className="w-12 px-1 py-1 text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Mais ações do item ${linha.loteInternoPieltec}`}
                className="size-11 sm:size-9"
              >
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="min-h-10" onSelect={() => onHistorico(linha)}>
                Histórico
              </DropdownMenuItem>
              {podeMovimentar && (
                <DropdownMenuItem className="min-h-10" onSelect={() => onTransferir(linha)}>
                  Transferir
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
      {aberta && (
        <tr id={idDetalhe} className="border-t bg-muted/50">
          <td colSpan={colSpan} className="px-4 py-3">
            <section aria-label="Lote" className="flex flex-col gap-3">
              <h3 className={ROTULO}>Lote</h3>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {!colunas.valor && (
                  <DadoExpandido rotulo="Material">
                    {linha.materialCodigo} — {linha.materialNome}
                  </DadoExpandido>
                )}
                {!colunas.valor && <DadoExpandido rotulo="Valor unit. (NF)">{formatarCustoUnitario(linha.valorUnitarioNf, '')}</DadoExpandido>}
                {!colunas.valor && <DadoExpandido rotulo="Valor (NF)">{formatarValor(linha.valorNf)}</DadoExpandido>}
                <DadoExpandido rotulo="OP">{linha.opNumero ?? '—'}</DadoExpandido>
                <DadoExpandido rotulo="NF de origem">{linha.nfOrigemNumero ?? '—'}</DadoExpandido>
                <DadoExpandido rotulo="Localização atual">{linha.localizacaoNome}</DadoExpandido>
                <DadoExpandido rotulo="Última mudança">
                  {linha.ultimaMudanca ? textoUltimaMudanca(linha.ultimaMudanca.evento, linha.ultimaMudanca.criadoEm) : 'Nenhuma mudança registrada'}
                </DadoExpandido>
              </dl>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onHistorico(linha)}>
                  Histórico
                </Button>
                {podeMovimentar && (
                  <Button type="button" variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onTransferir(linha)}>
                    Transferir
                  </Button>
                )}
              </div>
            </section>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Cabecalho({ colunas }: { colunas: ColunasVisiveis }) {
  return (
    <thead className="sticky top-0 z-10 bg-slate-100 text-left">
      <tr>
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Expandir</span>
        </th>
        <th scope="col" className={CABECALHO}>Lote</th>
        <th scope="col" className={CABECALHO}>Cliente</th>
        {colunas.valor && <th scope="col" className={CABECALHO}>Material</th>}
        <th scope="col" className={CABECALHO}>Localização</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Saldo</th>
        {colunas.valor && <th scope="col" className={`${CABECALHO} text-right`}>Valor unit. (NF)</th>}
        {colunas.valor && <th scope="col" className={`${CABECALHO} text-right`}>Valor (NF)</th>}
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Ações</span>
        </th>
      </tr>
    </thead>
  );
}

export interface SaldosClientesAbaProps {
  readonly filtros: FiltrosSaldosClientes;
  readonly aoAlterarFiltros: (parcial: Partial<FiltrosSaldosClientes>) => void;
  readonly aoLimparFiltros: () => void;
  /** Total de lotes do filtro (todas as páginas), para o subtítulo da tela; `null` enquanto não há resposta. */
  readonly aoAtualizarTotal: (total: number | null) => void;
  /** Só `true` com estoque:movimentar: sem a permissão, "Transferir" não é renderizado (a autorização real é do servidor). */
  readonly podeMovimentar: boolean;
  readonly aoHistorico: (linha: SaldoClienteLinha) => void;
  readonly aoTransferir: (linha: SaldoClienteLinha) => void;
  /** Muda a cada transferência registrada: a lista é lida de novo (o lote mudou de local). */
  readonly recarga?: number;
}

export function SaldosClientesAba({
  filtros,
  aoAlterarFiltros,
  aoLimparFiltros,
  aoAtualizarTotal,
  podeMovimentar,
  aoHistorico,
  aoTransferir,
  recarga = 0,
}: SaldosClientesAbaProps) {
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_SALDOS);
  const [dados, setDados] = useState<ListaSaldosClientes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());
  const colunas = useColunasVisiveis();

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/estoque/saldos/clientes?${montarQuerySaldosClientes(filtros, { pagina, tamanhoPagina })}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaSaldosClientes;
        if (!sinal || sinal.vivo) {
          setDados(json);
          setAbertas(new Set());
          aoAtualizarTotal(json.total);
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina, aoAtualizarTotal],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, recarga]);

  function alterar(parcial: Partial<FiltrosSaldosClientes>): void {
    aoAlterarFiltros(parcial);
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterar({ busca: buscaDigitada.trim() });
  }

  function limpar(): void {
    setBuscaDigitada('');
    setCliente(null);
    aoLimparFiltros();
    setPagina(1);
  }

  function alternar(chave: string): void {
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }

  const comFiltro = filtrosClientesAtivos(filtros);
  const estado = estadoDaLista({ carregando, erro, itens: dados?.itens, filtrosAtivos: comFiltro });
  // Cliente vindo da URL (sem o objeto do lookup): o combobox mostra um rótulo genérico até o usuário escolher outro.
  const clienteDoCombobox: ClienteLookupPv | null =
    filtros.clienteId === '' ? null : cliente?.id === filtros.clienteId ? cliente : { id: filtros.clienteId, codigo: '—', nome: 'Cliente filtrado' };
  const colSpan = 6 + (colunas.valor ? 3 : 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="sticky top-0 z-20 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              type="search"
              aria-label="Buscar material de clientes"
              placeholder="Buscar por lote, cliente ou código"
              value={buscaDigitada}
              onChange={(e) => setBuscaDigitada(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-56 flex-col gap-1">
            <label htmlFor="filtro-cliente" className={ROTULO}>
              Cliente
            </label>
            <ClienteCombobox
              id="filtro-cliente"
              endpoint={ENDPOINT_CLIENTES_ESTOQUE}
              value={clienteDoCombobox}
              onChange={(escolhido) => {
                setCliente(escolhido);
                alterar({ clienteId: escolhido.id });
              }}
            />
          </div>

          <div className="flex min-w-48 flex-col gap-1">
            <label htmlFor="filtro-localizacao-clientes" className={ROTULO}>
              Localização
            </label>
            <LocalizacaoSelect
              id="filtro-localizacao-clientes"
              rotulo="Localização"
              incluirTodas
              value={filtros.localizacaoId}
              onChange={(id) => alterar({ localizacaoId: id })}
            />
          </div>

          <label htmlFor="filtro-sem-saldo-clientes" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              id="filtro-sem-saldo-clientes"
              checked={filtros.mostrarSemSaldo}
              onCheckedChange={(marcada) => alterar({ mostrarSemSaldo: marcada === true })}
            />
            Mostrar itens sem saldo
          </label>

          {comFiltro && (
            <Button type="button" variant="ghost" onClick={limpar}>
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {estado === 'erro' ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o material de clientes. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : estado === 'carregando' ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando material de clientes">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
          <fieldset disabled className="opacity-60">
            <TablePagination
              page={pagina}
              pageSize={tamanhoPagina}
              totalItems={dados?.total ?? 0}
              onPageChange={setPagina}
              onPageSizeChange={setTamanhoPagina}
              itemLabel="lotes"
            />
          </fieldset>
        </div>
      ) : estado === 'vazio-sem-saldo' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum material de clientes com saldo</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Por padrão só aparecem lotes com saldo. Marque &quot;Mostrar itens sem saldo&quot; ou receba uma NF de entrada do cliente.
            </p>
          </CardContent>
        </Card>
      ) : estado === 'vazio-filtro' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum item encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" onClick={limpar}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <p className="border-b px-4 py-2 text-sm text-muted-foreground">{APOIO_VALOR_NF}</p>
          <table className="w-full text-sm">
            <caption className="sr-only">Material de clientes por lote e localização, valorizado pela NF de entrada</caption>
            <Cabecalho colunas={colunas} />
            <tbody>
              {(dados?.itens ?? []).map((linha) => (
                <LinhaLote
                  key={linha.loteId}
                  linha={linha}
                  aberta={abertas.has(linha.loteId)}
                  colunas={colunas}
                  colSpan={colSpan}
                  onAlternar={() => alternar(linha.loteId)}
                  onHistorico={aoHistorico}
                  podeMovimentar={podeMovimentar}
                  onTransferir={aoTransferir}
                />
              ))}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={dados?.total ?? 0}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="lotes"
          />
        </Card>
      )}
    </div>
  );
}
